import { supabase, isSupabaseAvailable } from './supabaseClient';
import { Profile, ProfessionalType } from '../types';

// ============================================
// PROFILE SERVICE
// ============================================

/**
 * Get current user's profile
 * Returns the complete profile including professional type
 */
export const getUserProfile = async (): Promise<Profile | null> => {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, cannot fetch profile');
    return null;
  }

  try {
    const { data: { user } } = await supabase!.auth.getUser();
    if (!user) {
      console.warn('No authenticated user, cannot fetch profile');
      return null;
    }

    const { data, error } = await supabase!
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }

    // Map snake_case from DB to camelCase for TypeScript
    const profile: Profile = {
      id: data.id,
      subscriptionTier: data.subscription_tier,
      testsLimitPerMonth: data.tests_limit_per_month,
      professionalType: data.professional_type || 'psychologist', // Map with default
      clinicianLimit: data.clinician_limit ?? undefined,
    };

    return profile;
  } catch (err) {
    console.error('Failed to fetch profile:', err);
    return null;
  }
};

/**
 * Update professional type for current user
 * @param professionalType - 'psychologist' or 'psychiatrist'
 * @returns true if successful, false otherwise
 */
export const updateProfessionalType = async (
  professionalType: ProfessionalType
): Promise<boolean> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available, cannot update professional type');
    return false;
  }

  try {
    const { data: { user } } = await supabase!.auth.getUser();
    if (!user) {
      console.error('No authenticated user, cannot update professional type');
      return false;
    }

    const { error } = await supabase!
      .from('profiles')
      .update({ professional_type: professionalType })
      .eq('id', user.id);

    if (error) {
      console.error('Error updating professional type:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Failed to update professional type:', err);
    return false;
  }
};
